/**
 * Workspace tab persistence, inside the CURRENT ACCOUNT'S data root (Tauri only).
 *
 *   <AppLocalData>/users/<accountId>/temp/workspace-tabs/{workspaceId}/tabs.json
 *   <AppLocalData>/users/<accountId>/temp/workspace-tabs/{workspaceId}/responses/{tabId}.json
 *
 * `tabs.json` is every open tab verbatim — URL, params, body, and the request
 * HEADERS, which is where `Authorization: Bearer …` lives; the response files
 * are whole response bodies. Exactly the class of data
 * `services/historyResponseStore.js` holds, so it is scoped exactly the same
 * way: through `accountPath()`, with the account root as part of every path
 * built here.
 *
 * It used to be a device-global `temp/workspace-tabs/{workspaceId}` keyed by
 * workspace id alone. Two members of one team workspace on one machine share
 * that key: quit without signing out, let the next account sign in and open the
 * shared workspace, and `loadWorkspaceTabs()` handed them the previous person's
 * tabs and bearer tokens. The account root is now in the path, so the id B
 * builds simply does not lead to A's file.
 *
 * There is deliberately no "clear every account's tabs" entry point any more.
 * Sign-out deletes the account root except its vault (`purgeAccountData`), so
 * these files go with it; a global wipe could only ever delete the WRONG
 * account's tabs (at logout the namespace has already flipped to `_local`).
 */
import { isTauri } from '../utils/tauri';
import { accountPath } from './accountData';
import {
  buildWorkspaceTabSnapshot,
  isValidWorkspaceTabSnapshot,
  normalizeSnapshot,
  sanitizeSnapshotForStorage,
  serializeResponse,
} from '../utils/workspaceTabPersistence';

const ROOT_REL = 'temp/workspace-tabs';
const RESPONSES_SUBDIR = 'responses';

let tabFsStorageCache;

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

/** Absolute tab root for the current account, or null when unavailable. */
async function rootDir() {
  return accountPath(ROOT_REL);
}

async function ensureDirAbs(absPath) {
  const fs = await fsModule();
  await fs.mkdir(absPath, { recursive: true });
}

/** Probe the current account's tab root (separate from local-workspaces cache). */
async function canPersistToFilesystem() {
  if (tabFsStorageCache !== undefined) return tabFsStorageCache;
  if (!isTauri()) {
    tabFsStorageCache = false;
    return false;
  }
  try {
    const root = await rootDir();
    if (!root) throw new Error('no account data dir');
    await ensureDirAbs(root);
    tabFsStorageCache = true;
  } catch (err) {
    console.warn('[workspaceTabStore] account tab storage unavailable:', err);
    tabFsStorageCache = false;
  }
  return tabFsStorageCache;
}

/** `<accountRoot>/temp/workspace-tabs/<workspaceId>`, or null. */
async function workspaceTempDir(workspaceId) {
  const root = await rootDir();
  return root ? `${root}/${String(workspaceId)}` : null;
}

async function tabsManifestPath(workspaceId) {
  const dir = await workspaceTempDir(workspaceId);
  return dir ? `${dir}/tabs.json` : null;
}

async function responsesDir(workspaceId) {
  const dir = await workspaceTempDir(workspaceId);
  return dir ? `${dir}/${RESPONSES_SUBDIR}` : null;
}

async function responseFilePath(workspaceId, tabId) {
  const dir = await responsesDir(workspaceId);
  return dir ? `${dir}/${String(tabId)}.json` : null;
}

function stripResponseForManifest(response) {
  if (!response || typeof response !== 'object') return null;
  return { ...response, data: undefined, external: true };
}

async function writeResponseFile(workspaceId, tabId, response) {
  if (!response) return;
  const dir = await responsesDir(workspaceId);
  const path = await responseFilePath(workspaceId, tabId);
  if (!dir || !path) return;
  const fs = await fsModule();
  await ensureDirAbs(dir);
  await fs.writeTextFile(path, JSON.stringify(serializeResponse(response)));
}

async function readResponseFile(workspaceId, tabId) {
  try {
    const path = await responseFilePath(workspaceId, tabId);
    if (!path) return null;
    const fs = await fsModule();
    return serializeResponse(JSON.parse(await fs.readTextFile(path)));
  } catch {
    return null;
  }
}

async function removeResponseFiles(workspaceId, tabIds) {
  if (!(await canPersistToFilesystem()) || !workspaceId || !tabIds?.length) return;
  const fs = await fsModule();
  await Promise.all(
    tabIds.map(async (tabId) => {
      try {
        const path = await responseFilePath(workspaceId, tabId);
        if (path) await fs.remove(path);
      } catch {
        // ignore missing
      }
    })
  );
}

async function responsesDirExists(workspaceId) {
  try {
    const dir = await responsesDir(workspaceId);
    if (!dir) return false;
    const fs = await fsModule();
    return fs.exists(dir);
  } catch {
    return false;
  }
}

async function pruneOrphanResponseFiles(workspaceId, activeTabIds) {
  if (!(await canPersistToFilesystem()) || !workspaceId) return;
  if (!(await responsesDirExists(workspaceId))) return;
  const active = new Set((activeTabIds || []).map(String));
  try {
    const fs = await fsModule();
    const dir = await responsesDir(workspaceId);
    if (!dir) return;
    const entries = await fs.readDir(dir);
    await Promise.all(
      entries.map(async (entry) => {
        if (!entry?.name?.endsWith('.json')) return;
        const tabId = entry.name.slice(0, -5);
        if (active.has(tabId)) return;
        try {
          await fs.remove(`${dir}/${entry.name}`);
        } catch {
          // ignore
        }
      })
    );
  } catch {
    // responses dir may not exist yet
  }
}

async function prepareManifestForFsSave(workspaceId, snapshot) {
  const manifest = sanitizeSnapshotForStorage(snapshot);
  const requestTabs = [];

  for (const tab of manifest.requestTabs || []) {
    if (!tab?.id) continue;
    if (tab.response) {
      await writeResponseFile(workspaceId, tab.id, tab.response);
      requestTabs.push({ ...tab, response: stripResponseForManifest(tab.response) });
    } else {
      requestTabs.push(tab);
    }
  }

  return { ...manifest, requestTabs };
}

async function hydrateManifestFromFs(workspaceId, manifest) {
  const requestTabs = await Promise.all(
    (manifest.requestTabs || []).map(async (tab) => {
      if (!tab?.id) return tab;
      const external = tab.response?.external;
      if (!external) return tab;
      const fromFile = await readResponseFile(workspaceId, tab.id);
      if (!fromFile) {
        return { ...tab, response: null };
      }
      return { ...tab, response: fromFile };
    })
  );
  return normalizeSnapshot({ ...manifest, requestTabs });
}

async function saveToFilesystem(workspaceId, snapshot) {
  const dir = await workspaceTempDir(workspaceId);
  const manifestPath = await tabsManifestPath(workspaceId);
  if (!dir || !manifestPath) return;
  await ensureDirAbs(dir);
  const fs = await fsModule();
  const manifest = await prepareManifestForFsSave(workspaceId, snapshot);
  await fs.writeTextFile(manifestPath, JSON.stringify(manifest));
  await pruneOrphanResponseFiles(
    workspaceId,
    manifest.requestTabs.map((t) => t.id)
  );
}

async function loadFromFilesystem(workspaceId) {
  try {
    const manifestPath = await tabsManifestPath(workspaceId);
    if (!manifestPath) return null;
    const fs = await fsModule();
    const manifest = JSON.parse(await fs.readTextFile(manifestPath));
    return hydrateManifestFromFs(workspaceId, manifest);
  } catch (err) {
    if (err?.message && !String(err.message).includes('not found')) {
      console.warn('[workspaceTabStore] Failed to load tabs for workspace', workspaceId, err);
    }
    return null;
  }
}

export async function saveWorkspaceTabs(workspaceId, stateOrSnapshot) {
  if (!workspaceId || !stateOrSnapshot) return;
  if (!(await canPersistToFilesystem())) return;

  const snapshot = stateOrSnapshot.version
    ? stateOrSnapshot
    : buildWorkspaceTabSnapshot(stateOrSnapshot);

  try {
    await saveToFilesystem(workspaceId, snapshot);
  } catch (error) {
    console.warn('[workspaceTabStore] Failed to persist workspace tabs:', error);
  }
}

export async function loadWorkspaceTabs(workspaceId) {
  if (!workspaceId) return null;
  if (!(await canPersistToFilesystem())) return null;

  try {
    return loadFromFilesystem(workspaceId);
  } catch (error) {
    console.warn('[workspaceTabStore] Failed to load workspace tabs:', error);
    return null;
  }
}

/** Remove stored response payload(s) for closed tab(s). */
export async function removeWorkspaceTabResponses(workspaceId, tabIds) {
  if (!workspaceId || !tabIds?.length) return;
  await removeResponseFiles(workspaceId, tabIds);
}

/** Clear all response files for a workspace (e.g. close all tabs). */
export async function clearWorkspaceTabResponses(workspaceId) {
  if (!workspaceId || !(await canPersistToFilesystem())) return;
  try {
    const dir = await responsesDir(workspaceId);
    if (!dir) return;
    const fs = await fsModule();
    await fs.remove(dir, { recursive: true });
  } catch {
    // ignore
  }
}

export async function removeWorkspaceTabs(workspaceId) {
  if (!workspaceId || !(await canPersistToFilesystem())) return;
  try {
    const dir = await workspaceTempDir(workspaceId);
    if (!dir) return;
    const fs = await fsModule();
    await fs.remove(dir, { recursive: true });
  } catch {
    // ignore
  }
}

export { isValidWorkspaceTabSnapshot, buildWorkspaceTabSnapshot };

/** @internal test helper */
export function __resetTabFsStorageCacheForTests() {
  tabFsStorageCache = undefined;
}
